// ConsentBanner.jsx - tells an administrator when the Power Platform
// consent is behind, before they need the capability it is missing.
//
// Entra grants a refresh token the permissions declared at the moment of
// consent. Add one later and every existing connection is quietly short
// of it. Without this, the way a customer finds out is by pressing Stop
// on a misbehaving agent and getting an error - which is the worst
// possible moment.
//
// States what is missing in terms of what they can do, not scope names,
// and takes one click to fix. Blocks nothing.

import { useEffect, useState } from 'react';
import { ShieldAlert, ExternalLink } from 'lucide-react';
import { supabase } from '@govern360/shared';

export default function ConsentBanner() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data?.session?.access_token;
        if (!token) return;
        const r = await fetch('/.netlify/functions/pp-consent-status', {
          headers: { Authorization: 'Bearer ' + token },
        });
        if (!r.ok) return;
        const j = await r.json();
        if (live && j.connected && !j.current) setState(j);
      } catch (e) { /* a banner that cannot load is not worth an error */ }
    })();
    return () => { live = false; };
  }, []);

  if (!state) return null;

  async function reauthorize() {
    setBusy(true);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      const r = await fetch('/.netlify/functions/pp-inventory-connect', {
        headers: { Authorization: 'Bearer ' + token },
      });
      const d = await r.json();
      if (!r.ok || !d.authorize_url) throw new Error(d.error || 'Could not start reauthorization.');
      window.location.href = d.authorize_url;
    } catch (e) {
      setBusy(false);
      // eslint-disable-next-line no-alert
      window.alert(e.message);
    }
  }

  return (
    <div className="banner warn" style={{ alignItems: 'flex-start', marginBottom: 14 }}>
      <ShieldAlert size={16} />
      <span style={{ flex: 1 }}>
        <strong>Your Power Platform connection is missing a permission.</strong>{' '}
        Reauthorize to {state.missing.join(', and ')}.
        {state.consented_at && (
          <span style={{ color: 'var(--g-text-2)' }}>
            {' '}Last authorized {new Date(state.consented_at).toLocaleDateString()}
            {state.consented_by ? ' by ' + state.consented_by : ''}.
          </span>
        )}
        <span style={{ color: 'var(--g-text-2)' }}>
          {' '}Nothing you have today stops working - this only adds what is listed.
          A Power Platform Administrator has to approve it.
        </span>
      </span>
      <button className="btn primary" style={{ height: 30, padding: '0 12px', fontSize: 12, whiteSpace: 'nowrap' }}
        disabled={busy} onClick={reauthorize}>
        <ExternalLink size={14} />{busy ? 'Opening...' : 'Reauthorize'}
      </button>
    </div>
  );
}